import { type ReactNode } from "react";

import { faBooks } from "@fortawesome/pro-duotone-svg-icons/faBooks";
import { faCommentLines } from "@fortawesome/pro-duotone-svg-icons/faCommentLines";
import { faFolderOpen } from "@fortawesome/pro-duotone-svg-icons/faFolderOpen";
import { faImage } from "@fortawesome/pro-duotone-svg-icons/faImage";

import {
  AssistantStep,
  SearchedFor,
  ThinkingStep,
  WritingSection,
} from "@styled/read_coverage";

import { type ChatReads } from "./use_assistant_chats";

// What a Drafting answer is writing now: a section for his review alone, or a
// report section.
export type DraftWriting = {
  writingNotes: boolean;
  writingSection: string | null;
};

// The assistant's current step for the line over the pane, when it's a
// search, a look at the case's files or a page image, thinking, or our
// references; otherwise null. On Drafting, `writing` wins once the server says
// it has written since its last call: that's its step. Once a references
// lookup returns, it's thinking, not still checking.
export const assistantStepLine = (
  reads: ChatReads,
  thinkingSeconds: number | null,
  writing?: DraftWriting,
): ReactNode => {
  const { readingNow, searchingNow, filesNow, checkingReferences } = reads;
  if (writing?.writingNotes) {
    return (
      <AssistantStep
        icon={faCommentLines}
        label="Writing for your review"
        detail="in the chat"
        hint="Writing for your review in the chat. This doesn't go in the report."
      />
    );
  }
  if (writing?.writingSection) {
    return <WritingSection name={writing.writingSection} />;
  }
  if (searchingNow) {
    return (
      <SearchedFor query={searchingNow.query} found={searchingNow.found} />
    );
  }
  if (filesNow !== null) {
    return (
      <AssistantStep
        icon={faFolderOpen}
        label="Looking at the case's files"
        detail={`${String(filesNow)} ${filesNow === 1 ? "file" : "files"}`}
      />
    );
  }
  if (readingNow?.image) {
    return (
      <AssistantStep
        icon={faImage}
        label="Looking at the page image"
        detail={`Page ${String(readingNow.pages[0])}`}
      />
    );
  }
  if (thinkingSeconds !== null) {
    return <ThinkingStep seconds={thinkingSeconds} />;
  }
  if (checkingReferences) {
    return <AssistantStep icon={faBooks} label="Checking references" />;
  }
  return null;
};
